/**
 * chartMath — pure, DOM-free chart geometry for the hand-rolled SVG charts.
 * Kept separate from the React components so the scale/ticks/path logic is unit
 * tested without rendering.
 */

export interface Padding {
  left: number;
  right: number;
  top: number;
  bottom: number;
}

export interface Point {
  x: number;
  y: number;
}

/** Round a value up to a "nice" 1/2/5 × 10^n — the axis STEP `niceScale` picks from. */
export function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = 10 ** exp;
  const fraction = value / base;
  let nice: number;
  if (fraction <= 1) nice = 1;
  else if (fraction <= 2) nice = 2;
  else if (fraction <= 5) nice = 5;
  else nice = 10;
  return nice * base;
}

export interface AxisScale {
  /** The top of the axis — always the last tick. */
  max: number;
  /** 0, step, 2·step … max. */
  ticks: number[];
}

/**
 * A step-based nice axis from zero: the STEP is the round number (1/2/5 × 10^n)
 * and the tick count follows from it, never the other way round.
 *
 * It replaced a 1/2/5 MAXIMUM divided into a fixed four ticks, which drew
 * 125/250/375 under a 500 axis for a peak of 309 (the data filling 62% of the
 * plot) and, for an all-zero series, a 0..1 axis whose quarter ticks a rounding
 * formatter printed as "0, 0, 1, 1, 1". `integer` (counts) keeps the step at 1
 * or more, so a count axis never shows a fractional tick; an all-zero series
 * gets 0..`target` in whole steps.
 */
export function niceScale(peak: number, integer: boolean, target = 4): AxisScale {
  const top = Number.isFinite(peak) && peak > 0 ? peak : 0;
  let step = top === 0 ? 1 : niceMax(top / target);
  if (integer) step = Math.max(1, step);
  // The epsilon keeps 0.3 / 0.1 (= 2.9999…) from growing a fourth step.
  const count = top === 0 ? target : Math.max(1, Math.ceil(top / step - 1e-9));
  const ticks = Array.from({ length: count + 1 }, (_, i) => Number((i * step).toPrecision(12)));
  return { max: ticks[count]!, ticks };
}

/**
 * Evenly distributed points + SVG path SEGMENTS for a single series.
 *
 * A NULL IS A GAP, AND THE LINE BREAKS AT IT — it is never plotted at zero and
 * never interpolated across. Both of those are assertions about a bucket the
 * reader was deliberately told nothing about: a zero says the organization did
 * nothing that week, and a straight line drawn over the hole says we measured
 * something in between. `GroupedChart` already refused to join across a blank
 * group for exactly this reason; this is the same refusal on the time axis.
 *
 * `points[i]` IS `null` FOR A GAP, so the array stays aligned to the input and
 * a caller can still ask "where would this point have been" for the labels and
 * the marked gap rule. `path` is the concatenation of the drawn runs; a run of
 * ONE measured point between two gaps produces no stroke (a line needs two
 * ends), so `dots` names the points that would otherwise be invisible.
 */
export function makeLinePath(
  values: readonly (number | null)[],
  width: number,
  height: number,
  max: number,
  pad: Padding,
): { points: (Point | null)[]; path: string; segments: string[]; dots: Point[] } {
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;
  const n = values.length;
  const safeMax = max > 0 ? max : 1;
  const xOf = (i: number): number =>
    n <= 1 ? pad.left + innerW / 2 : pad.left + (i / (n - 1)) * innerW;
  const points: (Point | null)[] = values.map((v, i) =>
    v === null ? null : { x: xOf(i), y: pad.top + innerH - (Math.max(0, v) / safeMax) * innerH },
  );

  const segments: string[] = [];
  const dots: Point[] = [];
  let run: Point[] = [];
  const flush = (): void => {
    if (run.length > 1) {
      segments.push(
        run.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
      );
    } else if (run.length === 1) {
      dots.push(run[0]!);
    }
    run = [];
  };
  for (const p of points) {
    if (p === null) flush();
    else run.push(p);
  }
  flush();

  return { points, path: segments.join(' '), segments, dots };
}

/** The x position of a bucket on the shared index space. */
export function xAt(index: number, count: number, width: number, pad: Padding): number {
  const innerW = width - pad.left - pad.right;
  return count <= 1 ? pad.left + innerW / 2 : pad.left + (index / (count - 1)) * innerW;
}
